import { useState, useEffect, useCallback } from "react";
import { C, Card, SectionTitle, Btn, Badge, Dot, Modal, Field } from "./theme";
import { GET, PATCH } from "./api";
import { useLang } from "./i18n";

export default function GpuStatus({ me }) {
  const { t } = useLang();
  const [gpuStatus, setGpuStatus] = useState([]);
  const [history, setHistory] = useState([]);
  const [tab, setTab] = useState("status");
  const [editUsage, setEditUsage] = useState(null);
  const [editForm, setEditForm] = useState({ reason: "", expected_end: "" });
  const [historyDays, setHistoryDays] = useState(30);

  const fetchData = useCallback(async () => {
    try {
      const [s, h] = await Promise.all([
        GET("/api/gpu-status"),
        GET(`/api/gpu-history?days=${historyDays}`),
      ]);
      setGpuStatus(s);
      setHistory(h);
    } catch (e) {}
  }, [historyDays]);

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, 10000);
    return () => clearInterval(id);
  }, [fetchData]);

  const openEdit = (gpu) => {
    setEditUsage(gpu);
    setEditForm({ reason: gpu.reason || "", expected_end: gpu.expected_end || "" });
  };

  const saveUsage = async () => {
    if (!editUsage?.usage_id) return;
    try {
      await PATCH(`/api/gpu-usage/${editUsage.usage_id}`, editForm);
      setEditUsage(null);
      fetchData();
    } catch (e) { alert(e.message); }
  };

  const fmtDate = (d) => {
    if (!d) return "-";
    const dt = new Date(d + "Z");
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const day = String(dt.getDate()).padStart(2, "0");
    const h = String(dt.getHours()).padStart(2, "0");
    const min = String(dt.getMinutes()).padStart(2, "0");
    return `${y}-${m}-${day} ${h}:${min} KST`;
  };

  const fmtDuration = (start, end) => {
    if (!start) return "-";
    const s = new Date(start + "Z");
    const e = end ? new Date(end + "Z") : new Date();
    const diff = Math.floor((e - s) / 1000);
    const days = Math.floor(diff / 86400);
    const hours = Math.floor((diff % 86400) / 3600);
    const mins = Math.floor((diff % 3600) / 60);
    if (days > 0) return `${days}${t("days")} ${hours}${t("hours")}`;
    if (hours > 0) return `${hours}${t("hours")} ${mins}${t("minutes")}`;
    return `${mins}${t("minutes")}`;
  };

  const thS = { padding: "8px 10px", textAlign: "left", fontSize: 9, color: C.dim2, letterSpacing: 1, fontWeight: 600 };
  const tdS = { padding: "7px 10px", fontSize: 11, fontFamily: "monospace" };

  return (
    <div>
      <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
        <Btn small variant={tab === "status" ? "cyan" : "ghost"} onClick={() => setTab("status")}>
          {t("tab_gpu_status")}
        </Btn>
        <Btn small variant={tab === "history" ? "cyan" : "ghost"} onClick={() => setTab("history")}>
          {t("gpu_history")}
        </Btn>
      </div>

      {tab === "status" && gpuStatus.length === 0 && (
        <Card style={{ overflow: "hidden" }}>
          {[...Array(8)].map((_, i) => (
            <div key={i} style={{
              display: "flex", gap: 16, padding: "10px 12px",
              borderBottom: `1px solid ${C.border}08`,
            }}>
              {[80, 50, 60, 70, 50, 40, 50, 80, 60, 70, 100].map((w, j) => (
                <div key={j} style={{
                  width: w, height: 14, borderRadius: 4,
                  background: `linear-gradient(90deg, ${C.border}44 25%, ${C.border}88 50%, ${C.border}44 75%)`,
                  backgroundSize: "200% 100%",
                  animation: "shimmer 1.5s infinite",
                }} />
              ))}
            </div>
          ))}
          <style>{`@keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
        </Card>
      )}

      {tab === "status" && gpuStatus.length > 0 && (
        <>
          <SectionTitle title={t("gpu_status")} />
          <Card style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                  {[t("server"),t("gpu"),t("status"),t("users"),t("vm"),t("gpu_util"),t("vram"),t("started"),t("elapsed"),t("expected_end"),t("reason"),""].map(h =>
                    <th key={h} style={thS}>{h}</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {gpuStatus.map(g => {
                  const isFree = !g.assigned_vm;
                  const isMine = g.assigned_user === me.id;
                  const utilColor = g.util >= 80 ? C.red : g.util >= 50 ? C.amber : g.util > 0 ? C.green : C.dim2;
                  return (
                    <tr key={`${g.server}-${g.gpu_id}`} style={{
                      borderBottom: `1px solid ${C.border}08`,
                      background: isMine ? C.cyan + "0a" : "transparent",
                      borderLeft: isMine ? `3px solid ${C.cyan}` : "3px solid transparent",
                    }}>
                      <td style={tdS}><span style={{ color: C.dim2 }}>{g.server_label}</span></td>
                      <td style={tdS}><span style={{ color: C.purple, fontWeight: 700 }}>GPU {g.gpu_id}</span></td>
                      <td style={tdS}>
                        {isFree
                          ? <Badge label={t("free")} color={C.green} />
                          : isMine
                          ? <Badge label="MY GPU" color={C.cyan} />
                          : <Badge label={t("in_use")} color={C.amber} />}
                      </td>
                      <td style={tdS}>
                        {g.assigned_user
                          ? <><span style={{ color: C.cyan }}>{g.assigned_user}</span>
                              {g.assigned_user_name && <span style={{ color: C.dim2 }}> ({g.assigned_user_name})</span>}</>
                          : <span style={{ color: C.dim2 }}>-</span>}
                      </td>
                      <td style={tdS}><span style={{ color: C.text }}>{g.assigned_vm || "-"}</span></td>
                      <td style={tdS}><span style={{ color: utilColor, fontWeight: 700 }}>{g.util > 0 ? `${g.util}%` : "-"}</span></td>
                      <td style={tdS}>
                        {g.mem_used > 0
                          ? <span style={{ color: C.blue }}>{(g.mem_used / 1024).toFixed(1)}/{(g.mem_total / 1024).toFixed(0)}G</span>
                          : <span style={{ color: C.dim2 }}>-</span>}
                      </td>
                      <td style={{ ...tdS, whiteSpace: "nowrap" }}>
                        <span style={{ color: C.text }}>{g.started_at ? fmtDate(g.started_at) : "-"}</span>
                      </td>
                      <td style={{ ...tdS, whiteSpace: "nowrap" }}>
                        <span style={{ color: C.amber }}>{g.started_at ? fmtDuration(g.started_at) : "-"}</span>
                      </td>
                      <td style={{ ...tdS, whiteSpace: "nowrap" }}>
                        <span style={{ color: g.expected_end ? C.amber : C.dim2 }}>
                          {g.expected_end ? fmtDate(g.expected_end) : isFree ? "-" : t("undecided")}
                        </span>
                      </td>
                      <td style={{ ...tdS, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {g.reason
                          ? <span style={{ color: C.text }}>{g.reason}</span>
                          : isFree ? <span style={{ color: C.dim2 }}>-</span>
                          : <span style={{ color: C.amber }}>{t("not_entered")}</span>}
                      </td>
                      <td style={tdS}>
                        {!isFree && g.usage_id && (me.role === "admin" || me.id === g.assigned_user) && (
                          <Btn small variant="ghost" onClick={() => openEdit(g)}>{t("edit")}</Btn>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        </>
      )}

      {tab === "history" && (
        <>
          <SectionTitle title={t("gpu_history")} action={
            <div style={{ display: "flex", gap: 4 }}>
              {[7, 30, 90].map(d => (
                <Btn key={d} small variant={historyDays === d ? "blue" : "ghost"}
                  onClick={() => setHistoryDays(d)}>{d}{t("days")}</Btn>
              ))}
            </div>
          } />
          <Card style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                  {[t("status"),t("server"),t("gpu"),t("users"),t("vm"),t("reason"),t("started"),t("ended"),t("usage_time")].map(h =>
                    <th key={h} style={thS}>{h}</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {history.map(h => (
                  <tr key={h.id} style={{ borderBottom: `1px solid ${C.border}08` }}>
                    <td style={tdS}>
                      <Badge label={h.status === "active" ? t("in_use") : t("ended")}
                        color={h.status === "active" ? C.green : C.dim2} />
                    </td>
                    <td style={tdS}><span style={{ color: C.dim2 }}>{h.server}</span></td>
                    <td style={tdS}><span style={{ color: C.purple, fontWeight: 700 }}>GPU {h.gpu_id}</span></td>
                    <td style={tdS}>
                      <span style={{ color: C.cyan }}>{h.user_id}</span>
                      {h.user_name && <span style={{ color: C.dim2 }}> ({h.user_name})</span>}
                    </td>
                    <td style={tdS}><span style={{ color: C.text }}>{h.vm_id}</span></td>
                    <td style={{ ...tdS, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: C.dim2 }}>
                      {h.reason || "-"}
                    </td>
                    <td style={{ ...tdS, whiteSpace: "nowrap", color: C.text }}>{fmtDate(h.started_at)}</td>
                    <td style={{ ...tdS, whiteSpace: "nowrap", color: C.text }}>{h.ended_at ? fmtDate(h.ended_at) : "-"}</td>
                    <td style={{ ...tdS, whiteSpace: "nowrap", color: C.amber }}>{fmtDuration(h.started_at, h.ended_at)}</td>
                  </tr>
                ))}
                {history.length === 0 && (
                  <tr><td colSpan={9} style={{ padding: 20, textAlign: "center", color: C.dim2 }}>
                    {t("no_history")}
                  </td></tr>
                )}
              </tbody>
            </table>
          </Card>
        </>
      )}

      {editUsage && (
        <Modal title={t("edit_gpu_usage")} onClose={() => setEditUsage(null)}>
          <div style={{ fontSize: 11, color: C.dim2, marginBottom: 14, fontFamily: "monospace" }}>
            {editUsage.server_label} GPU {editUsage.gpu_id} — {editUsage.assigned_user}
          </div>
          <Field label={t("usage_reason")} value={editForm.reason}
            onChange={v => setEditForm({ ...editForm, reason: v })}
            placeholder={t("usage_reason_ph")} />
          <Field label={t("expected_end")} value={editForm.expected_end}
            onChange={v => setEditForm({ ...editForm, expected_end: v })}
            placeholder={t("expected_end_ph")} />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
            <Btn variant="ghost" onClick={() => setEditUsage(null)}>{t("cancel")}</Btn>
            <Btn variant="cyan" onClick={saveUsage}>{t("save")}</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}
